import {
  useUpsertCompanyIntegrationMutation,
  useInitCaseConnectionMutation,
  InitCaseConnectionResponse,
} from '@/generated/codegen';
import useLocalSetting from '@/hooks/useLocalSetting';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import EmptyLayout from '@/components/Layout/EmptyLayout';
import { LoadingMessageCard } from '@/components/LoadingMessageCard';
import { CASE_OAUTH_CALLBACK_URL } from '@/amplify';
import { toast } from 'sonner';

const CASE_STATE_STORE = 'CASEAuthState';
const CLIENT_ID = import.meta.env.VITE_APP_CASE_CLIENT_ID;
const CONNECTION = 'CNHI-OPENID-CON-PROD';
const AUTH_SERVER = 'https://identity.cnhind.com/authorize';

// CASE y New Holland son marcas de CNH Industrial: comparten servidor OAuth,
// app del portal developer (mismo CLIENT_ID) y API. La marca queda definida
// por la cuenta con la que el usuario autoriza, y bajo qué provider se guarda
// la integración resultante.
export type CnhProvider = 'CASE' | 'NEW_HOLLAND';

const CNH_PROVIDER_LABELS: Record<CnhProvider, string> = {
  CASE: 'CASE',
  NEW_HOLLAND: 'New Holland',
};

export const useMakeCaseConnection = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setCaseState] = useLocalSetting(CASE_STATE_STORE, {});
  const location = useLocation();

  const startConnection = React.useCallback(
    async (companyId: number, provider: CnhProvider = 'CASE') => {
      if (!CLIENT_ID) {
        console.error('CNH OAuth CLIENT_ID is not configured');
        toast.error(
          `La integración ${CNH_PROVIDER_LABELS[provider]} no está configurada correctamente. Falta el CLIENT_ID.`
        );
        return;
      }
      const stateKey = uuid();
      setCaseState({
        authServer: AUTH_SERVER,
        stateKey,
        location,
        companyId,
        provider,
        step: 1,
      });

      // Build query string manually without encoding
      // This is not ideal but CASE OAuth server seems to have issues with encoded URLs
      const queryParams = [
        `client_id=${CLIENT_ID}`,
        `response_type=code`,
        `scope=offline_access`,
        `redirect_uri=${CASE_OAUTH_CALLBACK_URL}`,
        `state=${stateKey}`,
        `audience=https://api-data.cnh.com/`,
        `connection=${CONNECTION}`,
      ].join('&');

      window.location.replace(`${AUTH_SERVER}?${queryParams}`);
    },
    [location, setCaseState]
  );
  return { startConnection };
};

export const CaseOauthCallbackHandler = () => {
  const [caseState, setCaseState] = useLocalSetting<{
    authServer: string;
    stateKey: string;
    location: Location;
    code?: string;
    companyId: number;
    provider?: CnhProvider;
    step: number;
    credentials?: InitCaseConnectionResponse;
  }>(CASE_STATE_STORE);

  const history = useHistory();
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const stateKey = params.get('state');
  const [initCaseConnection] = useInitCaseConnectionMutation();
  const [upsertCompanyIntegration] = useUpsertCompanyIntegrationMutation();
  const providerLabel = CNH_PROVIDER_LABELS[caseState?.provider ?? 'CASE'];
  const handleAuthError = React.useCallback(
    (e: Error) => {
      toast.error(
        `Hubo un error intentando registrar la conexión con ${providerLabel}: ` +
          e.message
      );
      history.push(caseState?.location ?? '/');
    },
    [caseState?.location, history, providerLabel]
  );
  const [feedbackMessage, setFeedbackMessage] = React.useState<string>();

  React.useEffect(() => {
    try {
      if (!caseState) {
        handleAuthError(
          new Error('no se pudo encontrar una transacción en curso')
        );
      } else if (caseState.step === 1) {
        if (!code || !stateKey || caseState.stateKey !== stateKey) {
          toast.error('No recibimos los parámetros correctos');
          console.error({
            code,
            stateKey,
            caseState,
            location: window.location,
          });
          history.push('/');
          return;
        }
        setFeedbackMessage('Obteniendo código de autorización');
        setCaseState((prevState) => ({
          ...prevState!,
          code: code,
          step: 2,
        }));
        initCaseConnection({
          variables: { company_id: caseState.companyId, auth_code: code },
        })
          .then((response) => {
            if (!response.data?.initCaseConnection) {
              throw new Error(
                `No se recibieron credenciales de ${providerLabel}`
              );
            }
            const credentials = response.data?.initCaseConnection;
            setCaseState((prevState) => ({
              ...prevState!,
              credentials: credentials,
              step: 3,
            }));
          })
          .catch(handleAuthError);
      } else if (caseState.step === 3) {
        setFeedbackMessage('Registrando integración con Bilderit Connect');
        setCaseState((prevState) => ({
          ...prevState!,
          step: 4,
        }));
        // Strip __typename from credentials before sending to mutation
        const { __typename, ...credentialsWithoutTypename } =
          caseState.credentials!;
        upsertCompanyIntegration({
          variables: {
            input: {
              company_id: caseState.companyId,
              provider: caseState.provider ?? 'CASE',
              is_active: true,
              credentials: credentialsWithoutTypename,
            },
          },
        })
          .then(() => {
            toast.success('Integración registrada con éxito', {
              duration: 3000,
            });
            history.push(caseState.location);
          })
          .catch(handleAuthError);
      }
    } catch (e) {
      handleAuthError(e as Error);
    }
  }, [
    caseState,
    upsertCompanyIntegration,
    code,
    handleAuthError,
    history,
    initCaseConnection,
    providerLabel,
    setCaseState,
    stateKey,
  ]);

  return (
    <EmptyLayout>
      <div className="w-4/5 md:w-1/3 min-w-80 max-w-lg m-auto content-center">
        <LoadingMessageCard
          message={feedbackMessage ?? 'Procesando integración'}
        />
      </div>
    </EmptyLayout>
  );
};
